<script setup>
import {onMounted, ref, defineProps} from 'vue'
import moment from 'moment'

import User from '@/api/User'

import { useMainStore } from '@/stores/main'
const mainStore = useMainStore()

import { useQueryStore } from '@/stores/query'
let queryStore = useQueryStore()

import { useRouter } from 'vue-router'
const router = useRouter()

import AllQueriesView from '@/components/SlideOver/AllQueriesView.vue'

const props = defineProps({
  filterType: {
    type: String,
    default: null,
    validator: (value) => [null, 'ip', 'domain', 'event'].includes(value)
  }
})

const queries = ref([])

onMounted(async () => {
  await fetchQueries()
})

async function fetchQueries() {
  try {
    const response = await User.getQueries(0, 50, '', '', '', props.filterType || '')
    queries.value = response.data.queries || []
  } catch (e) {
    console.error('Failed to fetch queries', e)
    queries.value = []
  }
}

function clear_query() {
  queryStore.result = null
  queryStore.error = ""
  return true
}

async function exec_query(input, query_type) {
  // Route event queries to EventSearch view
  if (['event', 'event_search'].includes(query_type)) {
    router.push({
      name: 'EventSearch',
      query: {
        query: input.trim(),
        log: 'false' // Don't re-log historical queries
      }
    })
  } else if (query_type === 'domain') {
    router.push({ name: 'EnrichDomainResults', query: { input: input.trim(), log: 'false' } })
  } else if (query_type === 'domain_table') {
    router.push({ name: 'EnrichDomainTable', query: { input: input.trim(), log: 'false' } })
  } else if (query_type === 'ip') {
    router.push({ name: 'EnrichIpResults', query: { input: input.trim(), log: 'false' } })
  } else if (query_type === 'ip_table') {
    router.push({ name: 'EnrichIpTable', query: { input: input.trim(), log: 'false' } })
  } else {
    // Fallback for other query types
    let value = input.trim().split(",")
    router.push({
      name: 'SearchResults',
      query: {
        input: Array.isArray(value) ? value.join(',') : value,
        type: query_type,
        log: 'false'
      }
    })
  }
}

function showAllQueries() {
  mainStore.show_slideover(AllQueriesView, { filterType: props.filterType }, { width: 50 }, "Search History")
}

function pretty_print(query_type) {
  if (query_type == "ip") return "Ip"
  else if (query_type == "ip_table") return "Ip Bulk"
  else if (query_type == "subdomains") return "Subdomains"
  else if (query_type == "emails") return "Emails"
  else if (query_type == "similar") return "Similar domains"
  else if (query_type == "reverse_dns") return "Reverse DNS"
  else if (query_type == "fingerprint_ips") return "Fingerprint to Ips"
  else if (query_type == "ip_fingerprints") return "Ip fingerprints"
  else if (query_type == "signals_toptalkers") return "Signals Toptalkers"
  else if (query_type == "domain") return "Domain"
  else if (query_type == "domain_table") return "Domain Bulk"
  else if (query_type == "event" || query_type == "event_search") return "Netflow Search"
  else return query_type
}

function pretty_print_query(query_type, value) {
  if (query_type == "ip") return "(ip) " + value[0]
  else if (query_type == "ip_table") return "(ip_bulk) " + value.length
  else if (query_type == "subdomains") return "(subdomains) " + value[0]
  else if (query_type == "emails") return "(emails) " + value[0]
  else if (query_type == "similar") return "(similar) " +  value[0]
  else if (query_type == "reverse_dns") return "(rdns) " + value[0]
  else if (query_type == "fingerprint_ips") return "(ips) " + value[0]
  else if (query_type == "ip_fingerprints") return "(fingerprints) " + value[0]
  else if (query_type == "signals_toptalkers") return "(toptalkers)"
  else if (query_type == "domain") return "(domain) " + value[0]
  else if (query_type == "domain_table") return "(domain_bulk) " + value.length
  else if (query_type == "event" || query_type == "event_search") return "(event) " + value
  else return query_type
}
</script>


<template>
  <div class="b">
    <div v-if="queries.length > 0" class="history-content">
      <div class="history-header">
        <div class="history-label">Recent Searches</div>
        <button @click="showAllQueries" class="view-all-button">
          View All
          <font-awesome-icon icon="arrow-right" class="ms-1" />
        </button>
      </div>
      <div class="history-grid">
        <div
          v-for="query in queries"
          :key="query.id"
          class="history-chip"
          @click="exec_query(query.query, query.type)"
        >
          <div class="history-chip-left">
            <span class="history-type" :class="{ 'event-type': query.type === 'event' || query.type === 'event_search' }">{{ pretty_print(query.type) }}</span>
            <span class="history-time">{{ moment(query.timestamp).fromNow() }}</span>
          </div>
          <code class="history-query">{{ query.query }}</code>
          <div class="history-credits">{{ query.credits_used }} credits</div>
        </div>
      </div>
    </div>
    <div v-else class="empty-state">
      <font-awesome-icon icon="magnifying-glass" class="empty-icon" />
      <p class="empty-title">No search history</p>
      <p class="empty-description">Your search history will appear here</p>
    </div>
  </div>
</template>

<style scoped>
.b {
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.history-content {
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.history-header {
  flex-shrink: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
}

.history-label {
  color: #9CA3AF;
  font-size: 0.85rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.view-all-button {
  background-color: transparent;
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #6B7280;
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.view-all-button:hover {
  background-color: rgba(255, 255, 255, 0.05);
  color: #E5E7EB;
}

.history-grid {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 0.5rem;
}

.history-grid::-webkit-scrollbar {
  width: 6px;
}

.history-grid::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 3px;
}

.history-grid::-webkit-scrollbar-thumb {
  background: rgba(168, 85, 247, 0.3);
  border-radius: 3px;
}

.history-grid::-webkit-scrollbar-thumb:hover {
  background: rgba(168, 85, 247, 0.5);
}

.history-chip {
  background: rgba(26, 29, 36, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 6px;
  padding: 0.75rem 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 1rem;
}

.history-chip:hover {
  background: rgba(168, 85, 247, 0.08);
  border-color: rgba(168, 85, 247, 0.3);
}

.history-chip-left {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 150px;
}

.history-type {
  color: #a855f7;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.history-type.event-type {
  color: #3b82f6;
}

.history-time {
  color: #6B7280;
  font-size: 0.7rem;
}

.history-query {
  color: #c4b5fd;
  font-size: 0.875rem;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.history-credits {
  color: #6B7280;
  font-size: 0.75rem;
  text-align: right;
  white-space: nowrap;
}

.history-chip:hover .history-time,
.history-chip:hover .history-credits {
  color: #9CA3AF;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  color: #6B7280;
}

.empty-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.5;
}

.empty-title {
  font-size: 1rem;
  font-weight: 600;
  color: #9CA3AF;
  margin-bottom: 0.5rem;
}

.empty-description {
  font-size: 0.875rem;
  color: #6B7280;
  margin: 0;
}

</style>
